/**
 * Mapping v2 — typed reusable functions with a signature + strategy.
 *
 * A Mapping is a function declaration. The actual data-resolution
 * lives in one of three strategies (only `manual_table` ships a
 * usable resolver in this step; `db_lookup` and `http_lookup` arrive
 * in Steps D/E). Rows of a `manual_table` mapping live under
 * `mapping_rows` and are CRUD'd via `/mappings/{id}/rows`.
 */
import { api } from "./api";
import type { Paginated, PageSize } from "./pagination";

// ----- strategy + types ----------------------------------------------------

export type StrategyKind = "manual_table" | "db_lookup" | "http_lookup";

export type MappingFieldType = "string" | "integer" | "number" | "boolean";

export interface MappingField {
  name: string;
  type: MappingFieldType;
  /** Inputs default to `true` (required); outputs once the signature
   *  is locked default to `false` (nullable). */
  required: boolean;
  description?: string | null;
}

// ----- mapping CRUD shapes -------------------------------------------------

export interface MappingSummary {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  inputs: MappingField[];
  outputs: MappingField[];
  strategy_kind: StrategyKind;
  strategy_config: Record<string, unknown>;
  /** Flipped to true the first time a workflow active version
   *  references this mapping. After lock: signature edits are
   *  restricted to add-nullable-output only. */
  signature_locked: boolean;
  enabled: boolean;
  /** Row count for manual_table strategy. Always present in
   *  responses; not editable. */
  row_count: number;
  /** Whether the current strategy_config passes the strict per-strategy
   *  schema. False on freshly-created db_lookup / http_lookup
   *  mappings until the operator fills the strategy editor. The
   *  detail page uses this to render a "configure to enable" banner
   *  and lock the Enable toggle. */
  config_complete: boolean;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface MappingCreate {
  name: string;
  description?: string | null;
  tags?: string[];
  inputs: MappingField[];
  outputs: MappingField[];
  strategy_kind: StrategyKind;
  strategy_config?: Record<string, unknown>;
}

export interface MappingUpdate {
  description?: string | null;
  tags?: string[];
  inputs?: MappingField[];
  outputs?: MappingField[];
  strategy_config?: Record<string, unknown>;
  enabled?: boolean;
}

// ----- row CRUD shapes -----------------------------------------------------

export interface MappingRowSummary {
  id: string;
  mapping_id: string;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  created_at: string;
  updated_at: string | null;
}

export interface MappingRowWrite {
  inputs: Record<string, unknown>;
  outputs?: Record<string, unknown>;
}

// ----- client --------------------------------------------------------------

export const mappingsApi = {
  list: (params?: {
    strategy_kind?: StrategyKind;
    enabled?: boolean;
    page?: number;
    page_size?: PageSize;
  }) => api.get<Paginated<MappingSummary>>("/mappings", { query: params }),

  get: (id: string) => api.get<MappingSummary>(`/mappings/${id}`),

  create: (body: MappingCreate) =>
    api.post<MappingSummary>("/mappings", body),

  update: (id: string, body: MappingUpdate) =>
    api.patch<MappingSummary>(`/mappings/${id}`, body),

  delete: (id: string) => api.delete<void>(`/mappings/${id}`),

  // Rows — only meaningful for manual_table strategy.
  listRows: (
    mappingId: string,
    params?: { page?: number; page_size?: PageSize },
  ) =>
    api.get<Paginated<MappingRowSummary>>(
      `/mappings/${mappingId}/rows`,
      { query: params },
    ),

  createRow: (mappingId: string, body: MappingRowWrite) =>
    api.post<MappingRowSummary>(`/mappings/${mappingId}/rows`, body),

  updateRow: (mappingId: string, rowId: string, body: MappingRowWrite) =>
    api.patch<MappingRowSummary>(
      `/mappings/${mappingId}/rows/${rowId}`,
      body,
    ),

  deleteRow: (mappingId: string, rowId: string) =>
    api.delete<void>(`/mappings/${mappingId}/rows/${rowId}`),

  /** Test-resolve: invoke the mapping's resolver with explicit
   *  inputs and get the declared outputs (or a miss / error) back.
   *  Same code path the future ``call_mapping`` workflow node will
   *  use — what works here works there. Ignores the ``enabled``
   *  toggle on purpose so operators can validate before going live. */
  resolve: (mappingId: string, inputs: Record<string, unknown>) =>
    api.post<MappingResolveResponse>(
      `/mappings/${mappingId}/resolve`,
      { inputs },
    ),
};

export interface MappingResolveResponse {
  outputs: Record<string, unknown> | null;
  strategy_kind: StrategyKind;
  miss: boolean;
}

/** Strategy display metadata — used by the picker UI. Keeping it in
 *  the API module so the list + dialog + detail page share one source
 *  of truth on labels/descriptions/availability.
 *
 *  All three resolvers shipped in Mapping v2 Step 2; the `available`
 *  flag stays in the type for future strategies (e.g. graphql_lookup)
 *  that may be declared in the union before the resolver lands. */
export const STRATEGY_META: Record<
  StrategyKind,
  { label: string; description: string; available: boolean }
> = {
  manual_table: {
    label: "Manual table",
    description:
      "Inputs → outputs from a hand-maintained row table. Editable in the UI.",
    available: true,
  },
  db_lookup: {
    label: "Database lookup",
    description:
      "Parametrised SQL against an external Postgres / MySQL / SQL Server (read-only, SELECT or WITH).",
    available: true,
  },
  http_lookup: {
    label: "HTTP lookup",
    description:
      "Call an external HTTP endpoint with {input} templating and JSONata-extract the outputs.",
    available: true,
  },
};
